import { Bookmark, Globe } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { FirestoreSemanticData } from "reedy-data/models";
import { Badge } from "~/components/ui/badge";
import { Text } from "~/components/text";
import { OverviewTagCard } from "./overview-tag-card";
import {
  genreTags,
  splitParagraphs,
  withParagraphKeys,
  worldSettingTags,
} from "./utils";

interface OverviewSectionProps {
  contentLanguage: string | undefined;
  productTitle: string;
  semanticData: FirestoreSemanticData;
}

export const OverviewSection = ({
  contentLanguage,
  productTitle,
  semanticData,
}: OverviewSectionProps) => {
  const { t } = useTranslation(["productDetails"]);
  const genres = genreTags(semanticData.genres);
  const settingTags = worldSettingTags(semanticData.worldSettings);
  const paragraphs = splitParagraphs(semanticData.synopsis ?? "");
  const keyedParagraphs = withParagraphKeys(paragraphs);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center gap-3">
        <Text as="h2" variant="heading" className="text-balance">
          {t("productDetails:summary.overview.title", { title: productTitle })}
        </Text>
        <Badge>{t("productDetails:summary.overview.poweredBy")}</Badge>
      </div>

      {(genres.length > 0 || settingTags.length > 0) && (
        <div className="grid grid-cols-[repeat(auto-fit,minmax(260px,1fr))] gap-4">
          <OverviewTagCard
            contentLanguage={contentLanguage}
            icon={Bookmark}
            title={t("productDetails:summary.overview.genre")}
            tags={genres}
          />
          <OverviewTagCard
            contentLanguage={contentLanguage}
            icon={Globe}
            title={t("productDetails:summary.overview.worldSetting")}
            tags={settingTags}
          />
        </div>
      )}

      {paragraphs.length > 0 && (
        <div className="flex flex-col gap-3">
          <Text variant="overline">
            {t("productDetails:summary.overview.synopsis")}
          </Text>
          <div className="space-y-5 rounded-xl border border-secondary-200 bg-secondary-25 p-6">
            {keyedParagraphs.map((paragraph) => (
              <Text
                key={paragraph.key}
                variant="body"
                className="text-pretty"
                lang={contentLanguage}
              >
                {paragraph.text}
              </Text>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
